import { Injectable } from '@angular/core';

export interface RecordingSession {
  id: string;
  startTime: number;
  lastUpdated: number;
  mode: 'batch' | 'live';
  microphone: string;
  language: string;
  provider: string;
  model: string;
  sessionId: string;
  recordingDuration: number;
  pausedDuration: number;
  isPaused: boolean;
}

export interface AudioChunkData {
  sessionId: string;
  timestamp: number;
  chunkIndex: number;
  data: ArrayBuffer;
  size: number;
}

export interface TranscriptionData {
  sessionId: string;
  fullTranscript: string;
  interimTranscription: string;
  transcriptResponses: any[];
  wordCount: number;
  characterCount: number;
  lastUpdated: number;
}

@Injectable({
  providedIn: 'root'
})
export class AudiostreamingDbService {
  private dbName = 'AudioStreamingDB';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  constructor() {
    this.initDB();
  }

  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        // console.log('✅ IndexedDB opened successfully');
        resolve();
      };

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;

        // Create object stores if they don't exist
        if (!db.objectStoreNames.contains('sessions')) {
          const sessionStore = db.createObjectStore('sessions', { keyPath: 'id' });
          sessionStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
          // console.log('Created sessions store');
        }

        if (!db.objectStoreNames.contains('audioChunks')) {
          const chunkStore = db.createObjectStore('audioChunks', { autoIncrement: true });
          chunkStore.createIndex('sessionId', 'sessionId', { unique: false });
          chunkStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('Created audioChunks store');
        }

        if (!db.objectStoreNames.contains('transcriptions')) {
          const transcriptionStore = db.createObjectStore('transcriptions', { keyPath: 'sessionId' });
          transcriptionStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
          console.log('Created transcriptions store');
        }
      };
    });
  }

  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.initDB();
    }
    if (!this.db) {
      throw new Error('Failed to initialize IndexedDB');
    }
    return this.db;
  }

  // Session Management
  async saveSession(session: RecordingSession): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['sessions'], 'readwrite');
      const store = transaction.objectStore('sessions');
      const request = store.put(session);

      request.onsuccess = () => {
        //console.log(`💾 Session saved: ${session.id}`);
        resolve();
      };
      request.onerror = () => {
        console.error('Error saving session:', request.error);
        reject(request.error);
      };
    });
  }

  async getSession(id: string): Promise<RecordingSession | null> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['sessions'], 'readonly');
      const store = transaction.objectStore('sessions');
      const request = store.get(id);

      request.onsuccess = () => {
        resolve(request.result || null);
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  async getAllSessions(): Promise<RecordingSession[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['sessions'], 'readonly');
      const store = transaction.objectStore('sessions');
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  async deleteSession(id: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['sessions'], 'readwrite');
      const store = transaction.objectStore('sessions');
      const request = store.delete(id);

      request.onsuccess = () => {
        // console.log(`🗑️ Session deleted: ${id}`);
        resolve();
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  // Audio Chunk Management
  async saveAudioChunk(chunk: AudioChunkData): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['audioChunks'], 'readwrite');
      const store = transaction.objectStore('audioChunks');
      const request = store.add(chunk);

      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => {
        console.error('Error saving audio chunk:', request.error);
        reject(request.error);
      };
    });
  }

  async getAudioChunks(sessionId: string): Promise<AudioChunkData[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['audioChunks'], 'readonly');
      const store = transaction.objectStore('audioChunks');
      const index = store.index('sessionId');
      const request = index.getAll(sessionId);

      request.onsuccess = () => {
        const chunks = request.result || [];
        // Sort by chunkIndex
        chunks.sort((a, b) => a.chunkIndex - b.chunkIndex);
        resolve(chunks);
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  async deleteAudioChunks(sessionId: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['audioChunks'], 'readwrite');
      const store = transaction.objectStore('audioChunks');
      const index = store.index('sessionId');
      const request = index.getAllKeys(sessionId);

      request.onsuccess = () => {
        const keys = request.result;
        let deleteCount = 0;
        
        keys.forEach(key => {
          const deleteRequest = store.delete(key);
          deleteRequest.onsuccess = () => {
            deleteCount++;
            if (deleteCount === keys.length) {
              // console.log(`🗑️ Deleted ${deleteCount} audio chunks for session: ${sessionId}`);
              resolve();
            }
          };
        });

        if (keys.length === 0) {
          resolve();
        }
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  async getAudioChunksCount(sessionId: string): Promise<number> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['audioChunks'], 'readonly');
      const store = transaction.objectStore('audioChunks');
      const index = store.index('sessionId');
      const request = index.count(sessionId);

      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  // Transcription Management
  async saveTranscription(transcription: TranscriptionData): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['transcriptions'], 'readwrite');
      const store = transaction.objectStore('transcriptions');
      const request = store.put(transcription);

      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => {
        console.error('Error saving transcription:', request.error);
        reject(request.error);
      };
    });
  }

  async getTranscription(sessionId: string): Promise<TranscriptionData | null> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['transcriptions'], 'readonly');
      const store = transaction.objectStore('transcriptions');
      const request = store.get(sessionId);

      request.onsuccess = () => {
        resolve(request.result || null);
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  async deleteTranscription(sessionId: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['transcriptions'], 'readwrite');
      const store = transaction.objectStore('transcriptions');
      const request = store.delete(sessionId);

      request.onsuccess = () => {
        // console.log(`🗑️ Transcription deleted for session: ${sessionId}`);
        resolve();
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  // Cleanup entire session
  async cleanupSession(sessionId: string): Promise<void> {
    try {
      await this.deleteSession(sessionId);
      await this.deleteAudioChunks(sessionId);
      await this.deleteTranscription(sessionId);
      // console.log(`✅ Complete cleanup for session: ${sessionId}`);
    } catch (error) {
      console.error('Error during session cleanup:', error);
      throw error;
    }
  }

  // Check for pending sessions
  async hasPendingSessions(): Promise<boolean> {
    const sessions = await this.getAllSessions();
    return sessions.length > 0;
  }

  // Get session summary
  async getSessionSummary(sessionId: string): Promise<{ session: RecordingSession | null, chunkCount: number, transcription: TranscriptionData | null }> {
    const session = await this.getSession(sessionId);
    const chunkCount = await this.getAudioChunksCount(sessionId);
    const transcription = await this.getTranscription(sessionId);

    return { session, chunkCount, transcription };
  }
}
